import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { useAuth } from "../../../../hooks/use-auth";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type { Tenant } from "../../../../types/tenant";
import {
  APP_RESOURCES,
  AccessControl,
} from "../../../../permissions/access-control";

type FindAllTenantDto = {
  data: Tenant[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

type UsageTokenBucket = {
  input: number;
  output: number;
  cached: number;
};

type UsageTokens = {
  message: UsageTokenBucket;
  audio: UsageTokenBucket;
  embeddings: UsageTokenBucket;
  rewrites: UsageTokenBucket;
  image: UsageTokenBucket;
  file: UsageTokenBucket;
};

type TenantChargeDto = {
  usageTokens: UsageTokens;
  usageTotals: UsageTokenBucket;
  embeddingIngestionTokens: number;
  totalEmbeddingTokens: number;
  sessionMessages: number;
  receivedSessionMessages: number;
  sentSessionMessages: number;
  totalTemplateMessages: number;
  assistantTemplateMessages: number;
  attendantTemplateMessages: number;
  promptCost: number;
  cachedCost?: number;
  completionCost: number;
  embeddingCost: number;
  receivedSessionCost: number;
  sentSessionCost: number;
  whatsappCost: number;
  templateCost: number;
  totalUSD: number;
  modelCosts?: Record<
    string,
    {
      model: string;
      input: number;
      cached: number;
      output: number;
      inputCost: number;
      cachedCost: number;
      outputCost: number;
      totalCost: number;
    }
  >;
};

type ChargeQuery = {
  tenantName: string;
  year: number;
  month: number;
};

const monthOptions = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

function getMonthLabel(month: number): string {
  return (
    monthOptions.find((item) => item.value === month)?.label ?? String(month)
  );
}

function formatInteger(value: number): string {
  return new Intl.NumberFormat("pt-BR").format(value);
}

function formatUSD(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(value);
}

function hasTenantId(tenant: Tenant): tenant is Tenant & { id: string } {
  return typeof tenant.id === "string" && tenant.id.trim().length > 0;
}

const emptyUsageTokenBucket: UsageTokenBucket = {
  input: 0,
  output: 0,
  cached: 0,
};

const usageTokenLabels: Record<keyof UsageTokens, string> = {
  message: "Mensagens",
  audio: "Áudio",
  embeddings: "Embeddings",
  rewrites: "Reescritas RAG",
  image: "Imagens",
  file: "Arquivos",
  embeddings: "Embeddings",
};

const modelCostLabels: Record<string, string> = {
  message: "Mensagens",
  audio: "Áudio",
  rewrites: "Reescritas RAG",
  image: "Imagens",
  file: "Arquivos",
};

function normalizeUsageTokens(charge: TenantChargeDto): UsageTokens {
  return {
    message: charge.usageTokens.message ?? emptyUsageTokenBucket,
    audio: charge.usageTokens.audio ?? emptyUsageTokenBucket,
    embeddings: charge.usageTokens.embeddings ?? emptyUsageTokenBucket,
    rewrites: charge.usageTokens.rewrites ?? emptyUsageTokenBucket,
    image: charge.usageTokens.image ?? emptyUsageTokenBucket,
    file: charge.usageTokens.file ?? emptyUsageTokenBucket,
  };
}

function sumBuckets(buckets: UsageTokenBucket[]): UsageTokenBucket {
  return buckets.reduce(
    (total, bucket) => ({
      input: total.input + bucket.input,
      output: total.output + bucket.output,
      cached: total.cached + bucket.cached,
    }),
    { ...emptyUsageTokenBucket },
  );
}

export default function TenantChargePage() {
  const { user } = useAuth();
  const { error } = useToast();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [charge, setCharge] = useState<TenantChargeDto | null>(null);
  const [lastQuery, setLastQuery] = useState<ChargeQuery | null>(null);
  const [isLoadingCharge, setIsLoadingCharge] = useState(false);

  const canAccess = useMemo(
    () => AccessControl.canView(APP_RESOURCES.CONFIG_TENANT_CHARGE, user?.role),
    [user?.role],
  );
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, index) => currentYear - index);
  }, []);

  const tenantsWithId = useMemo(
    () =>
      tenants
        .filter(hasTenantId)
        .sort((a, b) => a.appName.localeCompare(b.appName, "pt-BR")),
    [tenants],
  );

  useEffect(() => {
    if (!canAccess) {
      setIsLoadingTenants(false);
      setTenants([]);
      return;
    }

    let isMounted = true;

    const fetchTenants = async () => {
      try {
        setIsLoadingTenants(true);

        const pageSize = 100;
        let pageNumber = 1;
        let totalPages = 1;
        const allTenants: Tenant[] = [];

        while (pageNumber <= totalPages) {
          const response = await api.get<FindAllTenantDto>("/tenant", {
            params: { page: pageNumber, limit: pageSize },
          });

          allTenants.push(...response.data.data);
          totalPages = response.data.totalPages;
          pageNumber += 1;
        }

        const uniqueTenants = Array.from(
          new Map(
            allTenants.map((tenant) => [tenant.id ?? tenant.appName, tenant]),
          ).values(),
        );

        if (!isMounted) return;
        setTenants(uniqueTenants);
        setSelectedTenantId((previous) => {
          if (
            previous &&
            uniqueTenants.some((tenant) => tenant.id === previous)
          ) {
            return previous;
          }

          if (
            user?.tenantId &&
            uniqueTenants.some((tenant) => tenant.id === user.tenantId)
          ) {
            return user.tenantId;
          }

          const firstTenant = uniqueTenants.find(hasTenantId);
          return firstTenant?.id ?? "";
        });
      } catch (err) {
        if (!isMounted) return;
        error(getApiErrorMessage(err));
        setTenants([]);
      } finally {
        if (!isMounted) return;
        setIsLoadingTenants(false);
      }
    };

    void fetchTenants();

    return () => {
      isMounted = false;
    };
  }, [canAccess, error, user?.tenantId]);

  const selectedTenant = useMemo(
    () =>
      tenantsWithId.find((tenant) => tenant.id === selectedTenantId) ?? null,
    [selectedTenantId, tenantsWithId],
  );
  const usageTokens = useMemo(
    () => (charge ? normalizeUsageTokens(charge) : null),
    [charge],
  );
  const usageRows = useMemo(() => {
    if (!usageTokens) return [];

    return (Object.keys(usageTokenLabels) as Array<keyof UsageTokens>).map(
      (category) => ({
        category,
        label: usageTokenLabels[category],
        ...usageTokens[category],
        total: usageTokens[category].input + usageTokens[category].output,
      }),
    );
  }, [usageTokens]);
  const usageTotals = useMemo(
    () =>
      usageTokens
        ? sumBuckets(Object.values(usageTokens))
        : { ...emptyUsageTokenBucket },
    [usageTokens],
  );

  function resetChargeData() {
    setCharge(null);
    setLastQuery(null);
  }

  async function fetchCharge() {
    if (!selectedTenantId) {
      error("Selecione um tenant para consultar o custo.");
      return;
    }

    try {
      setIsLoadingCharge(true);
      const response = await api.get<TenantChargeDto>("/report/charge", {
        params: {
          tenantId: selectedTenantId,
          year: selectedYear,
          month: selectedMonth,
        },
      });

      setCharge({
        ...response.data,
        totalTemplateMessages: response.data.totalTemplateMessages ?? 0,
        assistantTemplateMessages: response.data.assistantTemplateMessages ?? 0,
        attendantTemplateMessages: response.data.attendantTemplateMessages ?? 0,
        cachedCost: response.data.cachedCost ?? 0,
        templateCost: response.data.templateCost ?? 0,
      });
      setLastQuery({
        tenantName: selectedTenant?.appName ?? selectedTenantId,
        year: selectedYear,
        month: selectedMonth,
      });
    } catch (err) {
      setCharge(null);
      setLastQuery(null);
      error(getApiErrorMessage(err));
    } finally {
      setIsLoadingCharge(false);
    }
  }

  if (!canAccess) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-2">Custos por tenant</h1>
        <p className="app-page-subtitle">
          Você não tem permissão para acessar esta página.
        </p>
      </div>
    );
  }

  return (
    <div className="app-page-compact">
      <BackButton />
      <h1 className="app-page-title mb-2">Custos por tenant</h1>
      <p className="app-page-subtitle mb-6">
        Selecione os filtros para consultar o detalhamento de custos.
      </p>

      <div className="app-card mb-6 p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Tenant
            <select
              className="form-input disabled:bg-slate-100"
              value={selectedTenantId}
              onChange={(event) => {
                setSelectedTenantId(event.target.value);
                resetChargeData();
              }}
              disabled={isLoadingTenants || isLoadingCharge}
            >
              {isLoadingTenants && <option value="">Carregando...</option>}
              {!isLoadingTenants && tenantsWithId.length === 0 && (
                <option value="">Nenhum tenant disponível</option>
              )}
              {!isLoadingTenants &&
                tenantsWithId.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.appName}
                  </option>
                ))}
            </select>
          </label>

          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Ano
            <select
              className="form-input disabled:bg-slate-100"
              value={selectedYear}
              onChange={(event) => {
                setSelectedYear(Number(event.target.value));
                resetChargeData();
              }}
              disabled={isLoadingTenants || isLoadingCharge}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-2 text-sm text-slate-700">
            Mês
            <select
              className="form-input disabled:bg-slate-100"
              value={selectedMonth}
              onChange={(event) => {
                setSelectedMonth(Number(event.target.value));
                resetChargeData();
              }}
              disabled={isLoadingTenants || isLoadingCharge}
            >
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-4 flex justify-end">
          <LoadingButton
            onClick={fetchCharge}
            className="btn btn-neutral cursor-pointer"
            isLoading={isLoadingCharge}
            loadingLabel="Consultando"
            disabled={isLoadingTenants || !selectedTenantId}
          >
            <Search className="h-4 w-4" />
            Consultar custos
          </LoadingButton>
        </div>
      </div>

      {charge && lastQuery && (
        <div className="space-y-6">
          <section className="app-card p-4 sm:p-6">
            <p className="text-sm text-slate-600">
              Tenant:{" "}
              <span className="font-medium text-slate-800">
                {lastQuery.tenantName}
              </span>
            </p>
            <p className="text-sm text-slate-600 mb-4">
              Competência:{" "}
              <span className="font-medium text-slate-800">
                {getMonthLabel(lastQuery.month)}/{lastQuery.year}
              </span>
            </p>

            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-5">
              <p className="text-sm font-medium text-emerald-700">
                Custo total estimado
              </p>
              <p className="text-3xl font-bold text-emerald-900 mt-1">
                {formatUSD(charge.totalUSD)}
              </p>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
            <article className="app-card p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Input IA
              </p>
              <p className="text-xl font-semibold text-slate-800 mt-1">
                {formatUSD(charge.promptCost)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatInteger(charge.usageTotals.input)} tokens
              </p>
            </article>

            <article className="app-card p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Cached IA
              </p>
              <p className="text-xl font-semibold text-slate-800 mt-1">
                {formatUSD(charge.cachedCost ?? 0)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatInteger(charge.usageTotals.cached)} tokens
              </p>
            </article>

            <article className="app-card p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Output IA
              </p>
              <p className="text-xl font-semibold text-slate-800 mt-1">
                {formatUSD(charge.completionCost)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatInteger(charge.usageTotals.output)} tokens
              </p>
            </article>

            <article className="app-card p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Embedding
              </p>
              <p className="text-xl font-semibold text-slate-800 mt-1">
                {formatUSD(charge.embeddingCost)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatInteger(charge.totalEmbeddingTokens)} tokens
              </p>
            </article>

            <article className="app-card p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Mensagens recebidas
              </p>
              <p className="text-xl font-semibold text-slate-800 mt-1">
                {formatUSD(charge.receivedSessionCost)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatInteger(charge.receivedSessionMessages)} mensagens de
                contatos
              </p>
              <p className="mt-1 text-xs text-slate-500">
                USD 0,0010 por mensagem
              </p>
            </article>

            <article className="app-card p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Mensagens enviadas
              </p>
              <p className="text-xl font-semibold text-slate-800 mt-1">
                {formatUSD(charge.sentSessionCost)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatInteger(charge.sentSessionMessages)} mensagens aos
                contatos
              </p>
              <p className="mt-1 text-xs text-slate-500">
                USD 0,0010 + USD 0,0068 por mensagem
              </p>
            </article>

            <article className="app-card p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Templates
              </p>
              <p className="text-xl font-semibold text-slate-800 mt-1">
                {formatUSD(charge.templateCost)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatInteger(charge.totalTemplateMessages)} envios
              </p>
            </article>
          </section>

          {charge.modelCosts && (
            <section className="app-card overflow-hidden">
              <h2 className="app-section-title px-4 pt-4 sm:px-6 sm:pt-6">
                Modelos usados no cálculo
              </h2>

              <div className="overflow-x-auto p-4 sm:p-6">
                <table className="w-full table-auto divide-y divide-slate-200">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="pb-3 font-medium">Categoria</th>
                      <th className="pb-3 font-medium">Modelo</th>
                      <th className="pb-3 text-right font-medium">Input</th>
                      <th className="pb-3 text-right font-medium">Cached</th>
                      <th className="pb-3 text-right font-medium">Output</th>
                      <th className="pb-3 text-right font-medium">Custo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {Object.entries(charge.modelCosts).map(([key, item]) => (
                      <tr key={key}>
                        <td className="py-3 text-sm text-slate-600">
                          {modelCostLabels[key] ?? key}
                        </td>
                        <td className="py-3 text-sm font-semibold text-slate-800">
                          {item.model}
                        </td>
                        <td className="py-3 text-right text-sm text-slate-600">
                          {formatUSD(item.input)}
                        </td>
                        <td className="py-3 text-right text-sm text-slate-600">
                          {formatUSD(item.cached)}
                        </td>
                        <td className="py-3 text-right text-sm text-slate-600">
                          {formatUSD(item.output)}
                        </td>
                        <td className="py-3 text-right text-sm font-semibold text-slate-800">
                          {formatUSD(item.totalCost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="app-card overflow-hidden">
            <h2 className="app-section-title px-4 pt-4 sm:px-6 sm:pt-6">
              Tokens por categoria
            </h2>

            <div className="overflow-x-auto p-4 sm:p-6">
              <table className="w-full table-auto divide-y divide-slate-200">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="pb-3 font-medium">Categoria</th>
                    <th className="pb-3 text-right font-medium">Input</th>
                    <th className="pb-3 text-right font-medium">Output</th>
                    <th className="pb-3 text-right font-medium">Cached</th>
                    <th className="pb-3 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {usageRows.map((row) => (
                    <tr key={row.category}>
                      <td className="py-3 text-sm text-slate-600">
                        {row.label}
                      </td>
                      <td className="py-3 text-right text-sm font-semibold text-slate-800">
                        {formatInteger(row.input)}
                      </td>
                      <td className="py-3 text-right text-sm font-semibold text-slate-800">
                        {formatInteger(row.output)}
                      </td>
                      <td className="py-3 text-right text-sm font-semibold text-slate-800">
                        {formatInteger(row.cached)}
                      </td>
                      <td className="py-3 text-right text-sm font-semibold text-slate-800">
                        {formatInteger(row.total)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50">
                    <td className="py-3 text-sm font-semibold text-slate-800">
                      Total
                    </td>
                    <td className="py-3 text-right text-sm font-semibold text-slate-800">
                      {formatInteger(usageTotals.input)}
                    </td>
                    <td className="py-3 text-right text-sm font-semibold text-slate-800">
                      {formatInteger(usageTotals.output)}
                    </td>
                    <td className="py-3 text-right text-sm font-semibold text-slate-800">
                      {formatInteger(usageTotals.cached)}
                    </td>
                    <td className="py-3 text-right text-sm font-semibold text-slate-800">
                      {formatInteger(usageTotals.input + usageTotals.output)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="app-card overflow-hidden">
            <h2 className="app-section-title px-4 pt-4 sm:px-6 sm:pt-6">
              Outros consumos
            </h2>

            <div className="overflow-x-auto p-4 sm:p-6">
              <table className="w-full table-auto divide-y divide-slate-200">
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="py-3 text-sm text-slate-600">
                      Embeddings de ingestão
                    </td>
                    <td className="py-3 text-right text-sm font-semibold text-slate-800">
                      {formatInteger(charge.embeddingIngestionTokens)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-slate-600">
                      Mensagens recebidas dos contatos
                    </td>
                    <td className="py-3 text-right text-sm font-semibold text-slate-800">
                      {formatInteger(charge.receivedSessionMessages)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-slate-600">
                      Mensagens enviadas aos contatos
                    </td>
                    <td className="py-3 text-right text-sm font-semibold text-slate-800">
                      {formatInteger(charge.sentSessionMessages)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-slate-600">
                      Templates enviados por atendente
                    </td>
                    <td className="py-3 text-right text-sm font-semibold text-slate-800">
                      {formatInteger(charge.attendantTemplateMessages)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-slate-600">
                      Templates enviados por IA/Tenant
                    </td>
                    <td className="py-3 text-right text-sm font-semibold text-slate-800">
                      {formatInteger(charge.assistantTemplateMessages)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
